import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import api from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // RBAC role { key, name }
  const [permissions, setPermissions] = useState([]); // effective permission keys
  const [platformAdmin, setPlatformAdmin] = useState(false); // Radly platform admin (not client admin)
  const [loading, setLoading] = useState(true);

  const clearAuthStorage = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
  };

  // Fetch the signed-in user's RBAC role + effective permissions.
  const refreshPermissions = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setRole(res.data.role || null);
      setPermissions(
        Array.isArray(res.data.permissions) ? res.data.permissions : []
      );
      setPlatformAdmin(!!res.data.user?.isPlatformAdmin);
      return true;
    } catch (e) {
      setRole(null);
      setPermissions([]);
      setPlatformAdmin(false);
      return false;
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('adminToken');
      const savedUser = localStorage.getItem('adminUser');
      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          if (userData.role === 'admin' || userData.role === 'staff') {
            setUser(userData);
            await refreshPermissions();
          } else {
            clearAuthStorage();
          }
        } catch (e) {
          clearAuthStorage();
        }
      }
      setLoading(false);
    })();
  }, [refreshPermissions]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { accessToken, refreshToken, user: userData } = response.data;

      // Check if user is admin or staff
      if (userData.role !== 'admin' && userData.role !== 'staff') {
        throw new Error('Access denied. Admin privileges required.');
      }

      // Store both tokens
      localStorage.setItem('adminToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('adminRefreshToken', refreshToken);
      }
      localStorage.setItem('adminUser', JSON.stringify(userData));

      setUser(userData);
      await refreshPermissions();
      return { success: true };
    } catch (error) {
      const message =
        error.response?.data?.error || error.message || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    clearAuthStorage();
    setUser(null);
    setRole(null);
    setPermissions([]);
    setPlatformAdmin(false);
  };

  // Permission helpers. A '*' permission grants everything.
  const can = useCallback(
    (key) => permissions.includes('*') || permissions.includes(key),
    [permissions]
  );
  const canAny = useCallback(
    (keys) =>
      permissions.includes('*') ||
      (keys || []).some((k) => permissions.includes(k)),
    [permissions]
  );
  const canAll = useCallback(
    (keys) =>
      permissions.includes('*') ||
      (keys || []).every((k) => permissions.includes(k)),
    [permissions]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        permissions,
        loading,
        login,
        logout,
        refreshPermissions,
        isAdmin: user?.role === 'admin',
        isPlatformAdmin: platformAdmin,
        can,
        canAny,
        canAll,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
